import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap } from "lucide-react";
import { toast, Toaster } from "sonner";
import CustomCursor from "@/components/CustomCursor";
import AnimatedBackground from "@/components/AnimatedBackground";
import api from "@/lib/api";

export default function ResetPassword() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [newPassword, setNewPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (newPassword !== confirm) {
            toast.error("Passwords don't match");
            return;
        }
        if (newPassword.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, newPassword });
            setDone(true);
            setTimeout(() => navigate("/login"), 2500);
        } catch (err) {
            toast.error(err?.response?.data?.error?.message || "Reset failed — link may be expired");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex relative overflow-hidden bg-aura-bg">
            <Toaster theme="light" position="top-center" />
            <AnimatedBackground />
            <CustomCursor />

            <div className="hidden lg:flex flex-1 relative z-10 items-center justify-center px-20">
                <div className="max-w-md">
                    <div className="font-jetbrains-mono text-xs tracking-[0.4em] text-aura-violet mb-6">
                        // NEW ACCESS KEY
                    </div>
                    <h1 className="font-syne text-5xl font-bold leading-[1.05] mb-6 text-aura-text">
                        Create your
                        <br />
                        <span className="text-grad-violet">new password.</span>
                    </h1>
                    <p className="font-dm-sans text-aura-body text-base leading-relaxed">
                        Choose a strong password. Minimum 8 characters.
                    </p>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center px-6 lg:px-20 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="w-full max-w-sm glass-strong p-8"
                >
                    <Link to="/" className="flex items-center gap-2.5 mb-8">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-aura-violet to-aura-cyan flex items-center justify-center glow-violet-soft">
                            <Zap className="w-4 h-4 text-white" strokeWidth={2.5} />
                        </div>
                        <span className="font-syne font-bold text-lg text-aura-text">MirrorDigital</span>
                    </Link>

                    {done ? (
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
                            <div className="w-12 h-12 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mb-5">
                                <span className="text-green-400 text-xl">✓</span>
                            </div>
                            <h2 className="font-syne text-2xl font-bold mb-2 text-aura-text">Password updated</h2>
                            <p className="font-dm-sans text-sm text-aura-body">
                                Redirecting you to login…
                            </p>
                        </motion.div>
                    ) : (
                        <>
                            <h2 className="font-syne text-3xl font-bold mb-2 text-aura-text">New password</h2>
                            <p className="font-dm-sans text-sm text-aura-body mb-7">
                                Enter and confirm your new password below.
                            </p>

                            <form onSubmit={submit} className="space-y-4">
                                <div>
                                    <label className="font-jetbrains-mono text-[10px] uppercase tracking-[0.2em] text-aura-muted">
                                        New password
                                    </label>
                                    <input
                                        type="password"
                                        required
                                        minLength={8}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="Min. 8 characters"
                                        className="mt-1.5 w-full bg-aura-input border border-aura-violet/15 rounded-lg px-4 py-3 text-sm text-aura-text placeholder:text-aura-muted focus:border-aura-violet focus:outline-none focus:ring-2 focus:ring-aura-violet/20 transition"
                                    />
                                </div>
                                <div>
                                    <label className="font-jetbrains-mono text-[10px] uppercase tracking-[0.2em] text-aura-muted">
                                        Confirm password
                                    </label>
                                    <input
                                        type="password"
                                        required
                                        minLength={8}
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        placeholder="Repeat password"
                                        className="mt-1.5 w-full bg-aura-input border border-aura-violet/15 rounded-lg px-4 py-3 text-sm text-aura-text placeholder:text-aura-muted focus:border-aura-violet focus:outline-none focus:ring-2 focus:ring-aura-violet/20 transition"
                                    />
                                </div>

                                {/* Password match indicator */}
                                {confirm.length > 0 && (
                                    <p className={`text-xs font-jetbrains-mono ${newPassword === confirm ? "text-green-400" : "text-red-400"}`}>
                                        {newPassword === confirm ? "✓ Passwords match" : "✗ Passwords don't match"}
                                    </p>
                                )}

                                <motion.button
                                    type="submit"
                                    disabled={loading || newPassword !== confirm}
                                    whileTap={{ scale: 0.97 }}
                                    className="w-full bg-gradient-to-r from-aura-violet to-aura-glow text-white font-dm-sans font-medium py-3 rounded-lg glow-violet-soft btn-ripple disabled:opacity-60 transition"
                                >
                                    {loading ? "Updating…" : "Update password →"}
                                </motion.button>
                            </form>

                            <p className="mt-6 text-sm text-aura-body text-center">
                                <Link to="/login" className="text-aura-violet hover:text-aura-glow transition font-medium">
                                    Back to login
                                </Link>
                            </p>
                        </>
                    )}
                </motion.div>
            </div>
        </div>
    );
}
